import {
  type RouteObject,
  type DOMRouterOpts,
  type LoaderFunction,
  type LoaderFunctionArgs,
  replace,
} from 'react-router';
import * as betaGate from '~/app/beta-gate';
import * as root from '~/app/layout/root.page';
import { paths } from '~/common/app/paths';
import * as evaluatorDialect from '~/evaluator/view/evaluator-dialect';
import * as evaluatorModel from '~/evaluator/view/evaluator-model';
import * as evaluatorPrivacyPolicy from '~/evaluator/view/evaluator-privacy-policy';
import * as evaluatorToken from '~/evaluator/view/evaluator-token';
import { TokenErrorBoundary } from '~/evaluator/view/token-error-boundary';

export function createRoutes(
  children: RouteObject[] = routes(),
): RouteObject[] {
  return [
    {
      Component: root.Component,
      HydrateFallback: root.HydrateFallback,
      ErrorBoundary: root.ErrorBoundary,
      children,
    },
  ];
}

export function options(): DOMRouterOpts {
  return {
    basename: import.meta.env.BASE_URL,
  };
}

function routes(): RouteObject[] {
  return [
    {
      path: paths.confirmEmail.path,
      lazy: () =>
        import('~/evaluator-links/view/confirm-email.page').then(convert),
    },
    {
      path: paths.profileAccess.path,
      lazy: () =>
        import('~/profile-access/view/profile-access.page').then(convert),
    },
    {
      children: [
        {
          children: [
            {
              path: paths.exploreProfile.path,
              lazy: () =>
                import('~/profile/view/explore/home.page').then(convert),
            },
            {
              path: paths.exploreTheme.path,
              lazy: () =>
                import('~/profile/view/explore/theme.page').then(convert),
            },
            {
              path: '/profile/:profileId/:apiToken/*',
              loader: redirectExploreLegacyLink,
            },
            {
              path: '/exploredigitalProfile/*',
              loader: redirectExploreDigitalLink,
            },
          ],
        },
        {
          children: [
            {
              path: paths.discoveryProfile.path,
              lazy: () =>
                import('~/profile/view/discovery/home.page').then(convert),
            },
            {
              path: paths.discoveryInterview.path,
              lazy: () =>
                import('~/profile/view/discovery/interview.page').then(convert),
            },
            {
              path: paths.discoveryManagement.path,
              lazy: () =>
                import('~/profile/view/discovery/management.page').then(
                  convert,
                ),
            },
            {
              path: paths.discoveryPersonalAchievement.path,
              lazy: () =>
                import('~/profile/view/discovery/personal-achievement.page').then(
                  convert,
                ),
            },
            {
              path: paths.discoveryEffectiveSelling.path,
              lazy: () =>
                import('~/profile/view/discovery/effective-selling.page').then(
                  convert,
                ),
            },
            {
              path: paths.discoveryCriticalThinking.path,
              lazy: () =>
                import('~/profile/view/discovery/critical-thinking.page').then(
                  convert,
                ),
            },
            {
              path: paths.discoveryUnknownChapter.path,
              lazy: () =>
                import('~/profile/view/discovery/unknown-chapter.page').then(
                  convert,
                ),
            },
          ],
        },
        {
          middleware: [betaGate.betaOnly],
          ErrorBoundary: betaGate.ErrorBoundary,
          children: [
            {
              path: paths.impactProfile.path,
              lazy: () =>
                import('~/profile/view/impact/home.page').then(convert),
            },
          ],
        },
      ],
    },
    {
      path: '/invitation/*',
      middleware: [
        evaluatorDialect.applyQueryDialect,
        evaluatorToken.validateToken,
      ],
      ErrorBoundary: TokenErrorBoundary,
      lazy: () => import('~/evaluator/view/invitation.page').then(convert),
    },
    {
      middleware: [
        evaluatorDialect.applyQueryDialect,
        evaluatorToken.validateToken,
      ],
      ErrorBoundary: TokenErrorBoundary,
      lazy: () => import('~/evaluator/view/evaluator.page').then(convert),
      children: [
        {
          path: paths.discoveryEvaluator.path,
          middleware: [evaluatorModel.discoveryOnly],
          children: [
            {
              path: paths.discoveryEvaluatorWelcome.path,
              lazy: () =>
                import('~/evaluator/view/discovery/welcome.page').then(convert),
            },
            {
              middleware: [evaluatorPrivacyPolicy.discoveryAccepted],
              lazy: () =>
                import('~/evaluator/view/discovery/questionnaire.page').then(
                  convert,
                ),
              children: [
                {
                  path: paths.discoveryEvaluatorAboutYou1.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery/about-you-1.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryEvaluatorAboutYou2.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery/about-you-2.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryEvaluatorInstructions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery/instructions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryEvaluatorQuestions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery/questions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryEvaluatorComplete.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery/complete.page').then(
                      convert,
                    ),
                },
              ],
            },
          ],
        },
        {
          path: paths.discoveryApiEvaluator.path,
          middleware: [evaluatorModel.discoveryApiOnly],
          children: [
            {
              path: paths.discoveryApiEvaluatorWelcome.path,
              lazy: () =>
                import('~/evaluator/view/discovery-api/welcome.page').then(
                  convert,
                ),
            },
            {
              middleware: [evaluatorPrivacyPolicy.discoveryApiAccepted],
              lazy: () =>
                import('~/evaluator/view/discovery/questionnaire.page').then(
                  convert,
                ),
              children: [
                {
                  path: paths.discoveryApiEvaluatorAboutYou1.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-api/about-you-1.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryApiEvaluatorAboutYou2.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-api/about-you-2.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryApiEvaluatorInstructions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-api/instructions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryApiEvaluatorQuestions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-api/questions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryApiEvaluatorComplete.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-api/complete.page').then(
                      convert,
                    ),
                },
              ],
            },
          ],
        },
        {
          path: paths.exploreEvaluator.path,
          middleware: [evaluatorModel.exploreOnly],
          children: [
            {
              path: paths.exploreEvaluatorWelcome.path,
              lazy: () =>
                import('~/evaluator/view/explore/welcome.page').then(convert),
            },
            {
              middleware: [evaluatorPrivacyPolicy.exploreAccepted],
              lazy: () =>
                import('~/evaluator/view/discovery/questionnaire.page').then(
                  convert,
                ),
              children: [
                {
                  path: paths.exploreEvaluatorAboutYou1.path,
                  lazy: () =>
                    import('~/evaluator/view/explore/about-you-1.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.exploreEvaluatorAboutYou2.path,
                  lazy: () =>
                    import('~/evaluator/view/explore/about-you-2.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.exploreEvaluatorInstructions.path,
                  lazy: () =>
                    import('~/evaluator/view/explore/instructions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.exploreEvaluatorQuestions.path,
                  lazy: () =>
                    import('~/evaluator/view/explore/questions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.exploreEvaluatorComplete.path,
                  lazy: () =>
                    import('~/evaluator/view/explore/complete.page').then(
                      convert,
                    ),
                },
              ],
            },
          ],
        },
        {
          path: paths.discoveryFullCircleEvaluator.path,
          middleware: [evaluatorModel.discoveryFullCircleOnly],
          children: [
            {
              path: paths.discoveryFullCircleEvaluatorWelcome.path,
              lazy: () =>
                import('~/evaluator/view/discovery-full-circle/welcome.page').then(
                  convert,
                ),
            },
            {
              middleware: [evaluatorPrivacyPolicy.discoveryFullCircleAccepted],
              lazy: () =>
                import('~/evaluator/view/discovery-full-circle/questionnaire.page').then(
                  convert,
                ),
              children: [
                {
                  path: paths.discoveryFullCircleEvaluatorAboutYou.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-full-circle/about-you.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryFullCircleEvaluatorInstructions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-full-circle/instructions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryFullCircleEvaluatorQuestions.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-full-circle/questions.page').then(
                      convert,
                    ),
                },
                {
                  path: paths.discoveryFullCircleEvaluatorComplete.path,
                  lazy: () =>
                    import('~/evaluator/view/discovery-full-circle/complete.page').then(
                      convert,
                    ),
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: paths.tpaEvaluator.path,
      middleware: [evaluatorDialect.applyQueryDialect, betaGate.betaOnly],
      ErrorBoundary: betaGate.ErrorBoundary,
      lazy: () => import('~/evaluator/view/tpa/tpa.page').then(convert),
      children: [
        {
          path: paths.tpaEvaluatorWelcome.path,
          lazy: () => import('~/evaluator/view/tpa/welcome.page').then(convert),
        },
        {
          path: paths.tpaEvaluatorGetStarted.path,
          lazy: () =>
            import('~/evaluator/view/tpa/get-started.page').then(convert),
        },
        {
          path: paths.tpaEvaluatorComplete.path,
          lazy: () =>
            import('~/evaluator/view/tpa/complete.page').then(convert),
        },
      ],
    },
    {
      path: '*',
      loader: redirectToProfileAccess,
    },
  ];
}

function redirectExploreLegacyLink({ params, url }: LoaderFunctionArgs) {
  const searchParams = new URLSearchParams(url.search);
  searchParams.set('token', params.apiToken!);

  const pathname = paths.exploreProfile.href({ profileId: params.profileId! });
  const catchAll = params['*'];
  const subPath = catchAll ? `/${catchAll}` : '';

  return replace(`${pathname}${subPath}?${searchParams.toString()}`);
}

function redirectExploreDigitalLink({ params }: LoaderFunctionArgs) {
  return replace(`/profile/${params['*']}`);
}

function redirectToProfileAccess() {
  return replace(paths.profileAccess.href(undefined));
}

function convert(module: Module) {
  const { clientLoader, default: Component, ErrorBoundary } = module;
  return {
    loader: clientLoader,
    Component,
    ErrorBoundary,
  };
}

export type Module = {
  clientLoader?: LoaderFunction;
  default?: React.ComponentType;
  ErrorBoundary?: React.ComponentType;
};
